import exifr from 'exifr';

export function ensureFirebaseImageAccess(url: string): string {
   if (!url) return url;

   // Check if it's a Firebase Storage URL
   const isFirebaseUrl =
      url.includes('firebasestorage.googleapis.com') || url.includes('storage.googleapis.com');

   if (!isFirebaseUrl) return url;

   // Check if URL already has a token
   if (url.includes('token=')) return url;

   // If it's a Firebase URL without a token, we need to add alt=media
   // which makes it publicly accessible without authentication
   if (!url.includes('alt=media')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}alt=media`;
   }

   return url;
}

/**
 * Process an image URL to make sure it can be loaded without CORS issues
 *
 * @param url The image URL to process
 * @returns Processed URL ready for loading in an <img> tag
 */
export function processImageUrl(url: string): string {
   if (!url) return '';

   // First ensure Firebase access
   let processedUrl = ensureFirebaseImageAccess(url);

   // Add a cache-busting parameter for transformed images
   if (
      processedUrl.includes('transform=') ||
      processedUrl.includes('style=') ||
      processedUrl.includes('enhanced=')
   ) {
      const cacheBuster = `cb=${Date.now()}`;
      processedUrl = processedUrl.includes('?')
         ? `${processedUrl}&${cacheBuster}`
         : `${processedUrl}?${cacheBuster}`;
   }

   return processedUrl;
}

export const getImageTakenDate = async (file: File) => {
   try {
      ///get local image metadata not url
      const metadata = await exifr.parse(file);

      const dateTimeOriginal = new Date(metadata.DateTimeOriginal.toISOString());
      let location = null;
      if (metadata?.GPSLatitude && metadata?.GPSLongitude) {
         location = {
            latitude: metadata?.GPSLatitude,
            longitude: metadata?.GPSLongitude,
         };
      }

      return {
         takenDate: dateTimeOriginal,
         location,
      };
   } catch (error) {
      return {
         takenDate: null,
         location: null,
      };
   }
};

export const convertToJPEG = async (
   imageUrl: string
): Promise<{ blob: Blob; extension: string }> => {
   const response = await fetch(imageUrl);
   const originalBlob = await response.blob();
   const originalType = originalBlob.type.toLowerCase();

   const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

   if (allowedTypes.includes(originalType)) {
      const extension = originalType === 'image/png' ? 'png' : 'jpg';
      return { blob: originalBlob, extension };
   }

   // ❗️Otherwise, convert to JPEG using <canvas>
   return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
         const canvas = document.createElement('canvas');
         canvas.width = img.width;
         canvas.height = img.height;

         const ctx = canvas.getContext('2d');
         ctx?.drawImage(img, 0, 0);

         canvas.toBlob(
            (blob) => {
               if (blob) {
                  resolve({ blob, extension: 'jpg' });
               } else {
                  reject(new Error('Failed to convert image to JPEG'));
               }
            },
            'image/jpeg',
            0.95
         );
      };

      img.onerror = (err) => reject(err);
      img.src = imageUrl;
   });
};

export const objectURLToFile = async (objectUrl: any, fileName: string) => {
   const response = await fetch(objectUrl);
   const blob = await response.blob();

   return new File([blob], fileName, {
      type: blob.type,
      lastModified: Date.now(),
   });
};

const downLoadUrl = async (url: string) => {
   const response = await fetch(url);
   const blob = await response.blob();
   return blob;
};
